import type { ChatClarificationRequest } from '@nutians/shared/schemas';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ClarificationCardProps {
  clarification: ChatClarificationRequest;
  onSelect: (clarificationId: string, selected: unknown[]) => Promise<void>;
  readOnly?: boolean;
}

type SelectState = 'pending' | 'submitting' | 'submitted' | 'error';

export function ClarificationCard({
  clarification,
  onSelect,
  readOnly = false,
}: ClarificationCardProps) {
  const { t } = useTranslation('coach');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [state, setState] = useState<SelectState>('pending');
  const isMulti = clarification.mode === 'multi';

  async function handleSingleSelect(idx: number) {
    if (state !== 'pending' || readOnly) return;
    setState('submitting');
    try {
      await onSelect(clarification.id, [clarification.options[idx]?.value]);
      setState('submitted');
    } catch {
      setState('error');
    }
  }

  function toggleMulti(idx: number) {
    if (state !== 'pending' || readOnly) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  async function handleMultiConfirm() {
    if (selected.size === 0 || state !== 'pending') return;
    const values = [...selected].map((idx) => clarification.options[idx]?.value);
    setState('submitting');
    try {
      await onSelect(clarification.id, values);
      setState('submitted');
    } catch {
      setState('error');
    }
  }

  return (
    <aside
      className="mt-3 rounded-xl border border-mist/30 bg-mist/5 dark:border-mist/20 dark:bg-mist/5"
      aria-label={clarification.question}
    >
      <div className="px-4 py-3">
        {/* Free badge + question */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {!readOnly && state === 'pending' && (
            <span
              className="rounded-full bg-leaf/10 px-2 py-0.5 text-[10px] font-medium
                         text-leaf shrink-0"
            >
              {t('action.free')}
            </span>
          )}
          <p className="text-sm font-medium text-ink dark:text-ink">{clarification.question}</p>
        </div>

        {/* Result states */}
        {state === 'submitted' && (
          <p className="text-sm font-medium text-leaf dark:text-leaf">{t('clarification.done')}</p>
        )}
        {state === 'error' && (
          <p className="text-sm text-clay dark:text-clay" role="alert">
            {t('clarification.error')}
          </p>
        )}
        {readOnly && state === 'pending' && (
          <p className="text-sm text-ink-soft dark:text-ink-soft">{t('clarification.expired')}</p>
        )}

        {/* Options */}
        {(state === 'pending' || state === 'submitting') && !readOnly && (
          <>
            <div className="flex flex-wrap gap-2">
              {clarification.options.map((option, idx) => {
                const isSelected = isMulti && selected.has(idx);
                return (
                  <button
                    // biome-ignore lint/suspicious/noArrayIndexKey: options are order-stable
                    key={idx}
                    type="button"
                    onClick={() => (isMulti ? toggleMulti(idx) : void handleSingleSelect(idx))}
                    disabled={state === 'submitting'}
                    aria-pressed={isMulti ? isSelected : undefined}
                    className={`rounded-xl border px-3 py-2 text-sm font-medium transition-colors
                               min-h-[36px] focus:outline-none focus:ring-2 focus:ring-leaf
                               disabled:opacity-50
                               ${isSelected ? 'border-leaf bg-leaf text-white' : 'border-husk bg-shell text-ink hover:border-leaf/50 dark:border-ink/20 dark:bg-leaf dark:text-ink'}`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>

            {isMulti && selected.size > 0 && (
              <button
                type="button"
                onClick={() => void handleMultiConfirm()}
                disabled={state === 'submitting'}
                className="mt-3 rounded-lg bg-leaf px-4 py-1.5 text-sm font-medium text-white
                           disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-leaf"
              >
                {state === 'submitting'
                  ? t('clarification.submitting')
                  : t('clarification.confirm')}
              </button>
            )}
          </>
        )}
      </div>
    </aside>
  );
}
